import * as THREE from "three";
import { ARENA } from "@tank/shared";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";

/**
 * Owns the Three.js renderer, scene, camera, lighting and post-processing.
 * Real-time shadows, gradient sky, balanced lighting, tuned fog, and selective
 * bloom so emissive FX (projectiles, explosions, power-ups) glow.
 */
export class SceneManager {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  private composer: EffectComposer;

  constructor(container: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.shadowMap.enabled = true;
    // PCFSoftShadowMap was deprecated in three r185 (it silently falls back to
    // PCFShadowMap and warns each frame). Use PCFShadowMap directly.
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;
    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.scene.background = makeSkyGradient();
    this.scene.fog = new THREE.Fog(0x9fb4c4, ARENA.HALF * 1.2, ARENA.HALF * 3.0);

    this.camera = new THREE.PerspectiveCamera(
      55,
      container.clientWidth / container.clientHeight,
      0.1,
      500,
    );
    this.camera.position.set(0, 20, 30);
    this.camera.lookAt(0, 0, 0);

    // Lighting.
    this.scene.add(new THREE.HemisphereLight(0xdfe9f0, 0x2c3a1f, 0.7));

    const sun = new THREE.DirectionalLight(0xfff2d8, 1.5);
    sun.position.set(ARENA.HALF * 0.6, ARENA.HALF * 1.4, ARENA.HALF * 0.4);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    const s = ARENA.HALF * 1.1;
    sun.shadow.camera.left = -s;
    sun.shadow.camera.right = s;
    sun.shadow.camera.top = s;
    sun.shadow.camera.bottom = -s;
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = ARENA.HALF * 4;
    sun.shadow.bias = -0.0004;
    this.scene.add(sun);

    const fill = new THREE.DirectionalLight(0x88a0c0, 0.35);
    fill.position.set(-ARENA.HALF, ARENA.HALF, -ARENA.HALF);
    this.scene.add(fill);

    // Post-processing: render → bloom (selective, half-res) → output.
    const w = container.clientWidth;
    const h = container.clientHeight;
    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    const bloom = new UnrealBloomPass(new THREE.Vector2(w / 2, h / 2), 0.6, 0.5, 0.62);
    this.composer.addPass(bloom);
    this.composer.addPass(new OutputPass());
    this.composer.setSize(w, h);
  }

  render(): void {
    this.composer.render();
  }

  onResize(): void {
    const c = this.renderer.domElement.parentElement!;
    this.renderer.setSize(c.clientWidth, c.clientHeight);
    this.camera.aspect = c.clientWidth / c.clientHeight;
    this.camera.updateProjectionMatrix();
    this.composer.setSize(c.clientWidth, c.clientHeight);
  }
}

/** A vertical sky gradient as a background texture. */
function makeSkyGradient(): THREE.Texture {
  const c = document.createElement("canvas");
  c.width = 2;
  c.height = 256;
  const ctx = c.getContext("2d")!;
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  g.addColorStop(0, "#1b2738");
  g.addColorStop(0.55, "#3d5a75");
  g.addColorStop(1, "#9fb4c4");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 2, 256);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
